import { computed } from 'vue';

import { useCartStore } from '@/shared/pinia/modules/cart';
import { useConfigStore } from '@/shared/pinia/modules/config';
import { getCountryCode } from '@/shared/utils/country';

/**
 * Cart value for points calculation (sum of final_line_price for non-gift-card items).
 * Matches cart-value.liquid which uses discounted_price * quantity for non-gift-card items.
 * Cart API provides final_line_price which is the line total after all discounts.
 */
export function computeCartValueForPoints(items: ShopifyCartItem[]): number {
  if (!items?.length) return 0;

  return items.reduce((total, item) => {
    const isGiftCard = item.gift_card || item.product?.gift_card;
    if (isGiftCard) return total;
    return total + (item.final_line_price ?? 0);
  }, 0);
}

/**
 * Calculate points earned from cart value.
 * Uses Math.ceil for cart-level (non-line-item) to match Liquid points-calculation.
 * Excludes gift cards. Variants with loyalty_points_cost would require GraphQL - not excluded here.
 */
export function calculatePointsForCartValue(
  cartValueCents: number,
  pointsMultiplier: number
): number {
  if (pointsMultiplier <= 0) return 0;
  const points = (cartValueCents * pointsMultiplier) / 100;
  return Math.ceil(points);
}

/**
 * Format points for display (locale-aware, no currency).
 * Uses Intl.NumberFormat with locale-appropriate thousand separators.
 * Sweden: includes decimals. Others: whole numbers only.
 * Matches points-earned-formatted.liquid (money_without_currency - no currency symbol).
 */
export function formatPointsForDisplay(points: number): string {
  if (points <= 0) return '0';

  const locale = window.Hobbii?.storeLocale ?? 'en-US';
  const countryCode = getCountryCode();
  const pointsRounded = Math.ceil(points);

  const formatter = new Intl.NumberFormat(locale, {
    minimumFractionDigits: countryCode === 'SE' ? 2 : 0,
    maximumFractionDigits: countryCode === 'SE' ? 2 : 0
  });

  return formatter.format(pointsRounded);
}

/**
 * Composable for cart points calculation (earned points from cart value).
 * Used in MiniCartLinesSummary and similar components.
 */
export function useCartPoints() {
  const cartStore = useCartStore();
  const configStore = useConfigStore();

  const cartValue = computed(() => {
    const items = cartStore.cart?.items ?? [];
    return computeCartValueForPoints(items);
  });

  const points = computed(() => {
    return calculatePointsForCartValue(
      cartValue.value,
      configStore.pointsMultiplier
    );
  });

  const formattedPoints = computed(() => {
    return formatPointsForDisplay(points.value);
  });

  return {
    cartValue,
    points,
    formattedPoints,
    computeCartValueForPoints,
    calculatePointsForCartValue,
    formatPointsForDisplay
  };
}
